/**
 * Persistent in-memory cache for recipe data.
 * Persists across component remounts and tab switches.
 * Only refetch when cache is invalidated (create/update/delete) or empty.
 */

import type { Recipe } from '@/lib/recipes/types';

interface RecipesListCache {
  data: Recipe[];
  userId: string;
  timestamp: number;
}

const recipesListCache: { [userId: string]: RecipesListCache } = {};
const recipeByIdCache: { [recipeId: string]: Recipe } = {};
let cacheInvalidatedAt = 0;

/** Check if recipes list cache is valid (not invalidated, not empty) */
export function getCachedRecipes(userId: string): Recipe[] | null {
  if (!userId) return null;
  const cached = recipesListCache[userId];
  if (!cached) return null;
  if (cached.timestamp < cacheInvalidatedAt) return null;
  return cached.data;
}

/** Store recipes list in cache */
export function setCachedRecipes(userId: string, data: Recipe[]): void {
  if (!userId) return;
  recipesListCache[userId] = { data, userId, timestamp: Date.now() };
  data.forEach((r) => {
    recipeByIdCache[r.id] = r;
  });
}

/** Get a single recipe from cache (populated from list or single fetch) */
export function getCachedRecipe(recipeId: string): Recipe | null {
  return recipeByIdCache[recipeId] ?? null;
}

/** Store a single recipe in cache */
export function setCachedRecipe(recipe: Recipe): void {
  recipeByIdCache[recipe.id] = recipe;
}

/** Remove a recipe from cache (e.g. after delete) */
export function removeCachedRecipe(recipeId: string): void {
  delete recipeByIdCache[recipeId];
}

/** Update a recipe in the list cache and single-recipe cache (e.g. after patch) */
export function updateCachedRecipeInList(recipe: Recipe): void {
  recipeByIdCache[recipe.id] = recipe;
  for (const userId of Object.keys(recipesListCache)) {
    const cached = recipesListCache[userId];
    const idx = cached.data.findIndex((r) => r.id === recipe.id);
    if (idx >= 0) {
      cached.data[idx] = recipe;
      break;
    }
  }
}

/** Invalidate all recipe caches - call after create, update, or delete */
export function invalidateRecipesCache(): void {
  cacheInvalidatedAt = Date.now();
  Object.keys(recipesListCache).forEach((k) => delete recipesListCache[k]);
  Object.keys(recipeByIdCache).forEach((k) => delete recipeByIdCache[k]);
}

/** Check if cache was recently invalidated (within same tick) */
export function isCacheValid(): boolean {
  return cacheInvalidatedAt === 0;
}
